import { useState, useMemo, useCallback } from 'react';
import { startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { VoxRoi } from './VoxRoi';
import VoxRoiCostRail from './VoxRoiCostRail';
import { useVoxRoiConfig, useVoxRoiCatalog, useVoxRoiUsage, useUpdateVoxRoiConfig } from '../../hooks/useVoxRoi';
import { decryptConfigBlob, encryptConfigBlob } from '../../utils/crypto';
import { computeRoi } from './voxRoiCalc';
import { VoxRoiCostConfigData, VOX_ROI_CONFIG_VERSION, CostScheduleEntry } from '../../models/voxRoi';

const migrateConfig = (raw: Record<string, unknown>): VoxRoiCostConfigData => {
  if (raw.version === VOX_ROI_CONFIG_VERSION && 'costs' in raw) {
    return {
      version: VOX_ROI_CONFIG_VERSION,
      avgOrdersPerLoan: (raw.avgOrdersPerLoan as number) ?? 1.8,
      costs: raw.costs as Record<string, CostScheduleEntry[]>,
      beforeSelection: (raw.beforeSelection as string[]) ?? [],
    };
  }
  const productCosts = (raw.productCosts ?? {}) as Record<string, number>;
  const costs: Record<string, CostScheduleEntry[]> = {};
  for (const [key, cost] of Object.entries(productCosts)) {
    costs[key] = [{ effectiveFrom: '2020-01-01', effectiveTo: null, cost }];
  }
  return {
    version: VOX_ROI_CONFIG_VERSION,
    avgOrdersPerLoan: (raw.avgOrdersPerLoan as number) ?? 1.8,
    costs,
    beforeSelection: (raw.beforeSelection as string[]) ?? [],
  };
};

/** Sort each product's schedule entries by effectiveFrom ascending (once). */
const normalizeScheduleOrder = (config: VoxRoiCostConfigData): VoxRoiCostConfigData => {
  const sorted: Record<string, CostScheduleEntry[]> = {};
  for (const [key, schedule] of Object.entries(config.costs)) {
    sorted[key] = [...schedule].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  }
  return { ...config, costs: sorted };
};

const defaultDateRange = (): Date[] => {
  const now = new Date();
  return [startOfDay(startOfMonth(now)), endOfDay(endOfMonth(now))];
};

const VoxRoiContainer = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [decryptedConfig, setDecryptedConfig] = useState<VoxRoiCostConfigData | null>(null);
  const [dateRange, setDateRange] = useState<Date[] | undefined>(defaultDateRange);
  const [saveError, setSaveError] = useState<string | null>(null);

  const configQuery = useVoxRoiConfig();
  const catalogQuery = useVoxRoiCatalog();
  const updateConfigMutation = useUpdateVoxRoiConfig();

  const startDateIso = dateRange?.[0]?.toISOString();
  const endDateIso = dateRange?.[1]?.toISOString();
  const usageQuery = useVoxRoiUsage(
    isUnlocked ? startDateIso : undefined,
    isUnlocked ? endDateIso : undefined,
  );

  const configSettled = configQuery.isSuccess || configQuery.isError;
  const hasConfig = !!configQuery.data;

  const handleUnlock = useCallback(async () => {
    setUnlockError(null);
    if (!configSettled) {
      setUnlockError('Still loading configuration, please wait.');
      return;
    }
    if (configQuery.isError) {
      setUnlockError('Failed to load configuration. Please try again.');
      return;
    }
    if (!passphrase.trim()) {
      setUnlockError('Please enter a passphrase.');
      return;
    }

    if (!configQuery.data) {
      const emptyConfig: VoxRoiCostConfigData = {
        version: VOX_ROI_CONFIG_VERSION,
        avgOrdersPerLoan: 1.8,
        costs: {},
        beforeSelection: [],
      };
      try {
        const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(emptyConfig), passphrase });
        await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: VOX_ROI_CONFIG_VERSION });
        setDecryptedConfig(emptyConfig);
        setIsUnlocked(true);
      } catch {
        setUnlockError('Failed to create configuration.');
      }
      return;
    }

    let migrated: VoxRoiCostConfigData;
    try {
      const plaintext = await decryptConfigBlob({ blob: configQuery.data.encryptedData, passphrase });
      const raw = JSON.parse(plaintext);
      migrated = normalizeScheduleOrder(migrateConfig(raw));
    } catch {
      setUnlockError('Incorrect passphrase or corrupted data.');
      return;
    }

    try {
      if (migrated.version !== configQuery.data.version) {
        const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(migrated), passphrase });
        await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: migrated.version });
      }
    } catch {
      setUnlockError('Decryption succeeded but failed to save migrated configuration.');
      return;
    }

    setDecryptedConfig(migrated);
    setIsUnlocked(true);
  }, [passphrase, configQuery.data, configQuery.isError, configSettled, updateConfigMutation]);

  const result = useMemo(() => {
    if (!decryptedConfig || !catalogQuery.data || !usageQuery.data) return null;
    return computeRoi({ config: decryptedConfig, catalog: catalogQuery.data, usage: usageQuery.data });
  }, [decryptedConfig, catalogQuery.data, usageQuery.data]);

  const handleSaveConfig = useCallback(async (updatedConfig: VoxRoiCostConfigData) => {
    setSaveError(null);
    try {
      const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(updatedConfig), passphrase });
      await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: updatedConfig.version });
      setDecryptedConfig(updatedConfig);
    } catch {
      setSaveError('Failed to save configuration.');
    }
  }, [passphrase, updateConfigMutation]);

  return (
    <VoxRoi
      status={isUnlocked ? 'unlocked' : 'locked'}
      hasConfig={hasConfig}
      passphrase={passphrase}
      onPassphraseChange={setPassphrase}
      onUnlock={handleUnlock}
      unlockError={unlockError}
      isConfigLoading={!configSettled}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      result={result}
      isUsageLoading={usageQuery.isLoading}
      costRail={
        isUnlocked && decryptedConfig && catalogQuery.data ? (
          <VoxRoiCostRail
            config={decryptedConfig}
            catalog={catalogQuery.data}
            onSave={handleSaveConfig}
            isSaving={updateConfigMutation.isPending}
            saveError={saveError}
          />
        ) : undefined
      }
    />
  );
};

export default VoxRoiContainer;
